/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { getOwner } from '@ember/application';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import ReportTemplateType from '@linc-technologies/linced-common/enums/reports/report-template-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  COMPONENT_FIELD_MAP,
  TEXT_COMPONENT_TYPES,
  PROGRESS_COMPONENT_TYPES,
  PRINCIPAL_COMMENT_TEXT_TYPES,
  resolveLevelId,
} from 'linced-app/utils/reporting';
import {
  getCurrentMilestone,
  calculateYearLevelMilestoneDate,
} from '@linc-technologies/linced-common/utils/progressions';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';

export default class ReportingController extends Controller {
  // Services
  @service
  colours;

  @service
  dates;

  @service
  fetch;

  @service
  heroApi;

  @service
  options;

  @service
  reportingGroupBrowser;

  @service
  session;

  @service
  store;

  @service
  students;

  @service
  toaster;

  @optionAlias('hero:features:reporting')
  reportingEnabled;

  @computed('session.school.jurisdiction')
  get iframeUrl() {
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.NZ_MOE:
      case Jurisdiction.INDEPENDENT_SCHOOL:
        return 'https://info.our-hero.com/app/reporting-hub/nz';
      case Jurisdiction.AU_NSW:
        return 'https://info.our-hero.com/app/reporting-hub/nsw';
      default:
        return 'https://info.our-hero.com/app/reporting-hub';
    }
  }

  // Group browser shared state — lives in the service so the hub menu can access it
  @alias('reportingGroupBrowser.selectedGroupType')
  selectedGroupType;

  @alias('reportingGroupBrowser.groupSearch')
  groupSearch;

  // Plain getter, not @alias: expandedGroups is TrackedArray-backed on the
  // service, and a classic macro doesn't reliably observe its autotracked
  // reads. Read-only is fine - nothing ever reassigns this controller's own
  // `expandedGroups`, only mutates the underlying service array in place.
  get expandedGroups() {
    return this.reportingGroupBrowser.expandedGroups;
  }

  controllerFor(name) {
    return getOwner(this).lookup('controller:' + name);
  }

  @computed('session.{activeSchool,school}')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  editorOptions = Object.freeze({
    browser_spellcheck: true,
    content_style:
      "body { font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.5; } p { margin: 0 0 8px; } p:last-child { margin-bottom: 0; } ul, ol { margin: 0 0 8px; padding-left: 20px; }",
    elementpath: false,
    inline: false,
    menubar: false,
    min_height: 80,
    max_height: 400,
    plugins: 'lists',
    promotion: false,
    resize: false,
    statusbar: false,
    toolbar: 'bold italic underline | bullist numlist',
  });

  TEXT_COMPONENT_TYPES = TEXT_COMPONENT_TYPES;
  PROGRESS_COMPONENT_TYPES = PROGRESS_COMPONENT_TYPES;
  PRINCIPAL_COMMENT_TEXT_TYPES = PRINCIPAL_COMMENT_TEXT_TYPES;
  COMPONENT_FIELD_MAP = COMPONENT_FIELD_MAP;

  // The school's live report templates. Deliberately no period resolution
  // here: which period a template is currently in depends on the school's
  // rollover, so the backend answers that on the counts entry (periodIndex,
  // periodLabel, periodStartDate, periodEndDate) and the cards read it from
  // there. This list supplies the template records themselves.
  @computed(
    'model.reportTemplates.@each.{isActive,type,next,name}',
    'dates.currentAcademicYear',
  )
  get activeReportTemplates() {
    let year = this.dates.currentAcademicYear;
    return (this.get('model.reportTemplates') || [])
      .filter(
        (t) =>
          t.get('isActive') &&
          t.get('type') === ReportTemplateType.REPORT &&
          !t.get('next'),
      )
      .map((t) => {
        // Period-less default. Screens that know the period — the group
        // cards, and the write/review/view routes — overwrite this with one
        // naming it, since the record is shared.
        if (!t.get('displayName')) {
          t.set('displayName', `${year} ${t.get('name')}`);
        }
        return t;
      })
      .sort((a, b) => (a.get('name') || '').localeCompare(b.get('name') || ''));
  }

  _pendingProgressions = null;
  _progressionTimers = null;

  saveReportProgression(student, { judgement, effort, elementId }, subject) {
    if (!this._pendingProgressions) {
      this._pendingProgressions = {};
    }
    if (!this._progressionTimers) {
      this._progressionTimers = {};
    }

    let studentId = student.get('id');
    let type = judgement != null ? 'j' : 'e';
    let key = studentId + ':' + (elementId || 'overall') + ':' + type;

    this._pendingProgressions[key] = {
      student,
      judgement,
      effort,
      elementId,
      subject,
    };

    if (this._progressionTimers[key]) {
      clearTimeout(this._progressionTimers[key]);
    }
    this._progressionTimers[key] = setTimeout(() => {
      this._saveProgression(key);
    }, 1000);
  }

  _saveProgression(key) {
    let pending = this._pendingProgressions[key];
    if (!pending) {
      return;
    }
    let {
      student,
      judgement,
      effort,
      elementId,
      subject: pendingSubject,
    } = pending;
    delete this._pendingProgressions[key];
    delete this._progressionTimers[key];

    let groupWriteController = this.controllerFor('reporting.group.write');
    let studentWriteController = this.controllerFor(
      'reporting.group.student.write',
    );
    let writeController =
      (groupWriteController && groupWriteController.get('activeSection')
        ? groupWriteController
        : null) || studentWriteController;

    let subject =
      pendingSubject ||
      (writeController && writeController.get('activeSectionSubject'));
    if (!subject) {
      return;
    }

    let milestones = subject.get('milestones') || [];
    let currentMilestone = getCurrentMilestone(
      milestones,
      student,
      this.dates.rolloverMonthDate,
    );
    if (!currentMilestone) {
      return;
    }

    let subjectId = subject.get('id');
    let milestoneId = currentMilestone.get('id');

    let isUnspecified = judgement === 0 || effort === 0;
    let level = null;
    if (!isUnspecified) {
      let j = judgement != null ? judgement : effort;
      let resolvedId = resolveLevelId(j, subjectId, milestoneId, this.store);
      if (!resolvedId) {
        return;
      }
      level = this.store.peekRecord('milestones/level', resolvedId);
      if (!level) {
        return;
      }
    }

    let progressionData = {
      schoolId: this.session.school,
      personId: student,
      assessorId: this.session.person,
      milestoneId: currentMilestone,
      dueDate: this.getDueDate(student, currentMilestone),
      progressionType: ProgressionType.PROGRESSION,
      goalSubjectId: subject,
      elementId: elementId || undefined,
    };

    if (level) {
      progressionData.levelId = level;
    }

    if (judgement != null) {
      progressionData.judgement = judgement;
    }
    if (effort != null) {
      progressionData.effort = effort;
    }

    this.store
      .createRecord('milestones/progression', progressionData)
      .save()
      .then(() => {
        if (judgement != null && !elementId) {
          let wc =
            (groupWriteController && groupWriteController.get('activeSection')
              ? groupWriteController
              : null) || studentWriteController;
          if (wc) {
            wc.updateOtjRecords();
          }
        }
      })
      .catch(() => {
        this.toaster.error('Failed to save progression');
      });
  }

  // getDueDate calculates when an overall teacher judgement is due.
  getDueDate(person, milestone) {
    let dueDate = moment();
    let year = dueDate.year();

    if (milestone.type !== MilestoneType.YEAR_LEVEL) {
      if (isEmpty(person.firstSchooling)) {
        // No first schooling date defined, but we need one.
        // We shouldn't get here, but if we do, just return today.
        return dueDate.format('YYYY-MM-DD');
      } else {
        // Start with the first schooling date.
        // We'll add the number of weeks or months on to it.
        dueDate = moment(person.firstSchooling);
      }
    }

    switch (milestone.type) {
      case MilestoneType.WEEKS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'weeks');
        break;

      case MilestoneType.MONTHS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'months');
        break;

      case MilestoneType.YEAR_LEVEL:
        if (
          person.numericCurrentYearLevel === null ||
          person.numericCurrentYearLevel === undefined ||
          person.numericCurrentYearLevel === ''
        ) {
          // No current year level defined, but we need one to calculate
          // a year-level shift. We shouldn't get here, but if we do,
          // just return today.
          return dueDate.format('YYYY-MM-DD');
        }
        year = year - person.numericCurrentYearLevel + milestone.yearLevel;
        dueDate = calculateYearLevelMilestoneDate(
          year,
          milestone,
          this.dates.rolloverMonthDate,
        );
        break;
    }

    return dueDate.format('YYYY-MM-DD');
  }
}
